/**
 * Reload circuit-breaker.
 *
 * The app reloads on several recoverable failures (stale chunk after deploy,
 * service-worker takeover, dynamic-import preload error). Each guard was local,
 * so a persistent failure could thrash the page forever. This centralises every
 * loop-path reload: if we reload more than MAX_RELOADS times inside WINDOW_MS,
 * the breaker trips — we STOP reloading and let the app show a recovery screen
 * instead of refreshing endlessly.
 */

const TIMES_KEY = "slumbr:reload-times";
const TRIP_KEY = "slumbr:reload-tripped";
const WINDOW_MS = 30_000;
const MAX_RELOADS = 3;

/**
 * A same-route URL with a unique `_fresh` query param. Navigating here forces
 * the browser to MISS its HTTP cache for index.html (cache key includes the
 * query) and pull the live shell + current chunk hashes — the only reliable way
 * to escape a stale-index loop, since `location.reload()` happily reuses a
 * cached index. Path + hash are preserved so the user keeps their route.
 */
function freshUrl(): string {
  const u = new URL(window.location.href);
  u.searchParams.set("_fresh", Date.now().toString(36));
  return u.pathname + u.search + u.hash;
}

function recentReloads(): number[] {
  try {
    const raw = sessionStorage.getItem(TIMES_KEY);
    const arr = raw ? (JSON.parse(raw) as number[]) : [];
    const cutoff = Date.now() - WINDOW_MS;
    return Array.isArray(arr) ? arr.filter((t) => typeof t === "number" && t > cutoff) : [];
  } catch {
    return [];
  }
}

/**
 * Reload the page — unless we've already reloaded too many times recently, in
 * which case trip the breaker and do NOT reload. Returns true if it reloaded,
 * false if the breaker tripped (caller should stop and surface recovery UI).
 */
/**
 * Purge the service worker + its caches. Extracted from resetApp so a chunk-load failure can clear the
 * STALE WORKER on its first attempt instead of burning the whole reload budget against it.
 *
 * Why this is the right escalation: a precached shell pinned to purged chunk hashes cannot be fixed by
 * reloading — every reload is served by the same worker and fails identically, so the old behaviour spent
 * three doomed reloads and then showed a manual recovery button. Clearing first makes the single reload
 * that follows able to succeed. If the failure was NOT the worker's fault, the only cost is refetching the
 * offline cache once, which is far cheaper than a wedged app.
 */
const SKIP_SW_KEY = "slumbr:skip-sw-once";

/**
 * True exactly once after a purge: the load that follows recovery must run WITHOUT a service worker.
 *
 * Measured from a wedged tab: `navigator.serviceWorker.controller` was set, and fetching an asset path
 * that does not exist returned **200** — the SPA fallback hands back index.html for a missing file, which
 * is precisely what makes a module import fail with "Failed to fetch dynamically imported module". Purging
 * and then re-registering on the very next load gave the chunk request straight back to the worker that
 * had just failed it, so recovery reloaded forever. Skipping registration for one load lets that load go
 * to the network, which we know serves the chunk correctly; the worker returns on the load after.
 */
export function consumeSkipServiceWorker(): boolean {
  try {
    const skip = sessionStorage.getItem(SKIP_SW_KEY) === "1";
    if (skip) sessionStorage.removeItem(SKIP_SW_KEY);
    return skip;
  } catch {
    return false;
  }
}

export async function purgeServiceWorker(): Promise<void> {
  try {
    sessionStorage.setItem(SKIP_SW_KEY, "1");
  } catch { /* best-effort */ }
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch { /* best-effort — never block recovery on a purge failure */ }
}

/**
 * The ONE recovery policy for a chunk that failed to load: purge the worker, then reload past the HTTP
 * cache. Resolves true if a reload was started, false if the breaker tripped (caller must surface
 * recovery UI, because nothing further is going to happen on its own).
 *
 * Shared deliberately. Three separate paths can see a stale chunk — `vite:preloadError`, the `lazy()`
 * loader's catch, and the route error boundary — and only the first had learned to purge before
 * reloading. The other two plain-reloaded, so a precached shell pinned to purged hashes served the same
 * failure to every attempt, burned the whole reload budget, and left the boundary showing a "Refreshing
 * Slumbr..." spinner for a refresh that was never coming. Observed in production after a deploy.
 */
export async function recoverFromChunkLoad(): Promise<boolean> {
  await purgeServiceWorker();
  return guardedReload({ cacheBust: true });
}

export function guardedReload(opts?: { cacheBust?: boolean }): boolean {
  const times = recentReloads();
  if (times.length >= MAX_RELOADS) {
    try {
      sessionStorage.setItem(TRIP_KEY, "1");
    } catch {
      /* sessionStorage unavailable — nothing more we can do */
    }
    return false;
  }
  times.push(Date.now());
  try {
    sessionStorage.setItem(TIMES_KEY, JSON.stringify(times));
  } catch {
    /* ignore */
  }
  // Stale-deploy reloads must bypass the HTTP-cached index, or a pinned old
  // shell keeps reloading to itself. A plain reload is fine for clean updates.
  if (opts?.cacheBust) window.location.replace(freshUrl());
  else window.location.reload();
  return true;
}

/**
 * True once the breaker has tripped — boot should render the recovery screen.
 *
 * CONSUMES the flag. It used to be sticky with no exit but the manual button, and the only code that
 * cleared it (markAppStable) runs after a successful mount — which never happens while the recovery
 * screen is what's mounted. So a tab that tripped once kept showing "Slumbr got stuck updating" on every
 * later load even when the build was completely healthy and every request returned 200 (observed
 * exactly that). Reading it one-shot means the next load attempts normally: a genuinely broken build
 * re-trips within the same rapid-reload window, while a recovered one just proceeds.
 */
export function reloadLoopTripped(): boolean {
  try {
    const tripped = sessionStorage.getItem(TRIP_KEY) === "1";
    if (tripped) {
      // One-shot: never strand a healthy load behind it.
      sessionStorage.removeItem(TRIP_KEY);
      // The TALLY has to go too, or the flag re-arms itself and "one-shot" is a lie. Observed: the load
      // that renders this screen still fires `vite:preloadError` for the modulepreload links in the stale
      // index, whose handler calls guardedReload, which finds the tally still over MAX_RELOADS inside the
      // window, re-sets TRIP and declines — so every later load showed the recovery screen again, with a
      // clean URL and nothing reloading. Rendering this screen IS the circuit breaker doing its job: we
      // have stopped, so the count of how fast we were reloading has served its purpose.
      sessionStorage.removeItem(TIMES_KEY);
    }
    return tripped;
  } catch {
    return false;
  }
}

/**
 * Clear the rapid-reload tally after the app has rendered and stayed up, so
 * ordinary one-off reloads never accumulate toward the breaker. Only a genuine
 * loop (which never stays stable) keeps the count.
 */
export function markAppStable(): void {
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(TIMES_KEY);
      sessionStorage.removeItem(TRIP_KEY);
    } catch {
      /* ignore */
    }
  }, 8000);
}

/** Hard recovery: unregister service workers, purge caches, clear the breaker,
 *  then reload onto a clean slate. Wired to the recovery screen button. */
export async function resetApp(): Promise<void> {
  try {
    await purgeServiceWorker();
  } finally {
    try {
      sessionStorage.removeItem(TIMES_KEY);
      sessionStorage.removeItem(TRIP_KEY);
    } catch {
      /* ignore */
    }
    // Cache-bust so we fetch the LIVE index.html, not a stale HTTP-cached shell
    // (a plain reload here is exactly what left the user stuck).
    window.location.replace(freshUrl());
  }
}
